import { useCallback, useEffect, useState } from "react";
import { LuCopyPlus } from "react-icons/lu";
import { MdEventNote } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroAnimal from "../../components/modais/ModalCadastroAnimal";
import ModalCadastroTipoManejo from "../../components/modais/ModalCadastroTipoManejo";
import { api } from "../../services/api";
import { repositorioAnimal, repositorioEventoManejo } from "../../sync/repositories/pecuaria";
import { repositorioEstoqueItem, repositorioUnidadeMedida } from "../../sync/repositories/almoxarifado";

const AJUDA = {
  titulo: "Eventos de Manejo",
  descricao:
    "Registre aqui procedimentos de manejo aplicados a um animal (ex.: vacinação, vermifugação, exame veterinário). Isso mantém um histórico sanitário e de manejo completo por animal — e, quando o procedimento usa um produto do almoxarifado (vacina, medicamento, ração), dá baixa automática na quantidade consumida no estoque.",
  secoes: [
    {
      titulo: "Dados do Evento",
      campos: [
        {
          nome: "Animal",
          obrigatorio: true,
          explicacao: "O animal que recebeu o manejo.",
        },
        {
          nome: "Tipo de Manejo",
          obrigatorio: true,
          explicacao: "Categoria do procedimento realizado (cadastrada em Tipos de Manejo).",
        },
        {
          nome: "Data do Manejo",
          obrigatorio: true,
          explicacao: "Data em que o procedimento foi realizado.",
        },
        {
          nome: "Observações",
          obrigatorio: false,
          explicacao: "Detalhes do procedimento: dosagem, resultado, próxima data prevista, etc.",
        },
      ],
    },
    {
      titulo: "Insumos Utilizados",
      campos: [
        {
          nome: "Produto",
          obrigatorio: false,
          explicacao: "Item do estoque consumido no procedimento (ex.: uma vacina ou medicamento). Deixe em branco se o manejo não usou nenhum produto do almoxarifado.",
        },
        {
          nome: "Quantidade",
          obrigatorio: false,
          explicacao: "Quanto desse produto foi aplicado/consumido — dá baixa nessa quantidade no estoque ao salvar.",
        },
      ],
    },
  ],
  dicas: [
    "Cadastrar, editar ou excluir um evento de manejo exige conexão com a internet — apenas a consulta funciona offline.",
    "Se o evento usar um produto do estoque, a baixa é automática ao salvar; editar as quantidades ou excluir o evento devolve o insumo correspondente ao estoque.",
    "É possível adicionar vários insumos no mesmo evento com o botão 'Adicionar Insumo'.",
  ],
};

const ITEM_INSUMO_VAZIO = {
  produto: "",
  produto_nome: "",
  quantidade_consumida: "",
};

export default function GestaoEventosManejo() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [eventos, setEventos] = useState([]);
  const [eventosFiltrados, setEventosFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  // Dados para os selects
  const [animais, setAnimais] = useState([]);
  const [tiposManejo, setTiposManejo] = useState([]);
  const [mostrarModalAnimal, setMostrarModalAnimal] = useState(false);
  const [mostrarModalTipoManejo, setMostrarModalTipoManejo] = useState(false);
  const [produtos, setProdutos] = useState([]);
  const [unidadesMedida, setUnidadesMedida] = useState([]);

  const [filtros, setFiltros] = useState({
    busca: "",
    data_inicio: "",
    data_fim: ""
  });

  const [formData, setFormData] = useState({
    animal: "",
    tipo_manejo: "",
    data_manejo: "",
    observacoes: "",
  });

  const [itensInsumo, setItensInsumo] = useState([{ ...ITEM_INSUMO_VAZIO }]);

  // Carregar animais
  const carregarAnimais = useCallback(async () => {
    try {
      const response = await repositorioAnimal.listar();
      const dados = response.data.results || response.data;
      setAnimais(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar animais:", error);
      setAnimais([]);
    }
  }, []);

  // Carregar tipos de manejo
  const carregarTiposManejo = useCallback(async () => {
    try {
      const response = await api.get("/pecuaria/tipos_manejo/");
      const dados = response.data.results || response.data;
      setTiposManejo(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar tipos de manejo:", error);
      setTiposManejo([]);
    }
  }, []);

  // Carregar eventos de manejo
  const carregarEventos = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioEventoManejo.listar();
      const dados = response.data.results || response.data;
      setEventos(Array.isArray(dados) ? dados : []);
      setEventosFiltrados(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar eventos:", error);
      alert("Erro ao carregar lista de eventos de manejo");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  // Carregar produtos do almoxarifado (insumos consumíveis no manejo)
  const carregarProdutos = useCallback(async () => {
    try {
      const [produtosRes, unidadesRes] = await Promise.all([
        repositorioEstoqueItem.listar(),
        repositorioUnidadeMedida.listar(),
      ]);
      setProdutos(produtosRes.data?.results || produtosRes.data || []);
      setUnidadesMedida(unidadesRes.data?.results || unidadesRes.data || []);
    } catch (error) {
      console.error("Erro ao carregar produtos:", error);
      setProdutos([]);
    }
  }, []);

  useEffect(() => {
    carregarEventos();
    carregarAnimais();
    carregarTiposManejo();
    carregarProdutos();
  }, [carregarEventos, carregarAnimais, carregarTiposManejo, carregarProdutos]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setItensInsumo((prev) => {
      const atualizado = prev.map((item, i) => (i === index ? { ...item, [field]: value } : item));
      if (field === "produto") {
        const produto = produtos.find((p) => String(p.id) === String(value));
        atualizado[index].produto_nome = produto?.nome || "";
      }
      return atualizado;
    });
  };

  const addInsumo = () => {
    setItensInsumo((prev) => [...prev, { ...ITEM_INSUMO_VAZIO }]);
  };

  const removeInsumo = (index) => {
    setItensInsumo((prev) => {
      const atualizado = prev.filter((_, i) => i !== index);
      return atualizado.length > 0 ? atualizado : [{ ...ITEM_INSUMO_VAZIO }];
    });
  };

  const validarFormulario = () => {
    if (!formData.animal) {
      alert("Selecione um animal.");
      return false;
    }
    if (!formData.tipo_manejo) {
      alert("Selecione o tipo de manejo.");
      return false;
    }
    if (!formData.data_manejo) {
      alert("Informe a data do manejo.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      animal: formData.animal,
      tipo_manejo: formData.tipo_manejo,
      data_manejo: formData.data_manejo,
      observacoes: formData.observacoes || "",
      itens_insumo: itensInsumo
        .filter((i) => i.produto && i.quantidade_consumida)
        .map((i) => ({
          ...(i.id ? { id: i.id } : {}),
          produto: Number(i.produto),
          quantidade_consumida: Number(i.quantidade_consumida),
        })),
    };

    try {
      if (editando) {
        await api.put(`/pecuaria/eventos_manejo/${editandoId}/`, payload);
        alert("Evento de manejo atualizado com sucesso!");
        handleCancel();
        carregarEventos();
      } else {
        await api.post("/pecuaria/eventos_manejo/", payload);
        alert("Evento de manejo registrado com sucesso!");
        handleCancel();
        carregarEventos();
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (evento) => {
    setFormData({
      animal: evento.animal?.id || evento.animal || "",
      tipo_manejo: evento.tipo_manejo?.id || evento.tipo_manejo || "",
      data_manejo: evento.data_manejo || "",
      observacoes: evento.observacoes || "",
    });

    if (evento.itens_insumo && evento.itens_insumo.length > 0) {
      setItensInsumo(
        evento.itens_insumo.map((item) => ({
          id: item.id,
          produto: item.produto?.id || item.produto || "",
          produto_nome: item.produto_nome || item.produto?.nome || "",
          quantidade_consumida: item.quantidade_consumida || "",
        })),
      );
    } else {
      setItensInsumo([{ ...ITEM_INSUMO_VAZIO }]);
    }

    setEditando(true);
    setEditandoId(evento.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovoEvento = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      animal: "",
      tipo_manejo: "",
      data_manejo: "",
      observacoes: "",
    });
    setItensInsumo([{ ...ITEM_INSUMO_VAZIO }]);
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este evento? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await api.delete(`/pecuaria/eventos_manejo/${id}/`);
      alert("Evento excluído com sucesso!");
      carregarEventos();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir evento.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(eventos) ? [...eventos] : [];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(e => 
        e.animal_sisbov?.toLowerCase().includes(buscaLower) ||
        e.animal_nome?.toLowerCase().includes(buscaLower) ||
        e.animal?.nome?.toLowerCase().includes(buscaLower) ||
        e.tipo_manejo_nome?.toLowerCase().includes(buscaLower) ||
        e.tipo_manejo?.nome?.toLowerCase().includes(buscaLower)
      );
    }
    
    if (filtros.data_inicio) {
      filtrados = filtrados.filter(e => e.data_manejo >= filtros.data_inicio);
    }
    
    if (filtros.data_fim) {
      filtrados = filtrados.filter(e => e.data_manejo <= filtros.data_fim);
    }
    
    setEventosFiltrados(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", data_inicio: "", data_fim: "" });
    setEventosFiltrados(Array.isArray(eventos) ? eventos : []);
  };

  const formatarData = (data) => {
    if (!data) return "—";
    return new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  const getAnimalIdentificacao = (evento) => {
    // Tenta obter o nome do animal de diferentes formas
    const nome = evento.animal_nome || evento.animal?.nome || "Sem nome";
    const sisbov = evento.animal_sisbov || evento.animal?.sisbov || evento.animal?.numero_identificacao || "";
    
    if (sisbov) {
      return `${sisbov} - ${nome}`;
    }
    return nome;
  };

  const getTipoManejoNome = (evento) => {
    return evento.tipo_manejo_nome || evento.tipo_manejo?.nome || "—";
  };

  // Função para formatar a exibição do animal no select
  const getAnimalDisplay = (animal) => {
    const identificacao = animal.sisbov || animal.numero_identificacao || `ID: ${animal.id}`;
    const nome = animal.nome || "Sem nome";
    return `${identificacao} - ${nome}`;
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Eventos de Manejo <MdEventNote size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoEvento}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Evento
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Evento de Manejo" : "Registrar Novo Evento de Manejo"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Animal e Tipo de Manejo */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Animal *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="animal"
                        value={formData.animal}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione um animal...</option>
                        {Array.isArray(animais) && animais.map(animal => (
                          <option key={animal.id} value={animal.id}>
                            {getAnimalDisplay(animal)}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalAnimal(true)}
                        title="Cadastrar novo animal"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Tipo de Manejo *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="tipo_manejo"
                        value={formData.tipo_manejo}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione o tipo de manejo...</option>
                        {Array.isArray(tiposManejo) && tiposManejo.map(tipo => (
                          <option key={tipo.id} value={tipo.id}>
                            {tipo.nome}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalTipoManejo(true)}
                        title="Cadastrar novo tipo de manejo"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Linha 2: Data do Manejo */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data do Manejo *</label>
                    <input
                      type="date"
                      name="data_manejo"
                      value={formData.data_manejo}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 w-64"
                      required
                    />
                  </div>
                </div>

                {/* Insumos utilizados */}
                <div className="border-t pt-4">
                  <h3 className="mb-4 text-lg font-semibold text-gray-800">Insumos Utilizados</h3>

                  {itensInsumo.map((item, index) => (
                    <div key={index} className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="flex flex-col">
                          <label className="mb-1 text-sm font-medium text-gray-700">Produto</label>
                          <select
                            value={item.produto}
                            onChange={(e) => handleItemChange(index, "produto", e.target.value)}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          >
                            <option value="">Nenhum</option>
                            {produtos.map((p) => (
                              <option key={p.id} value={p.id}>{p.nome}</option>
                            ))}
                          </select>
                        </div>

                        <div className="flex flex-col">
                          <label className="mb-1 text-sm font-medium text-gray-700">Quantidade</label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.quantidade_consumida}
                            onChange={(e) => handleItemChange(index, "quantidade_consumida", e.target.value)}
                            disabled={!item.produto}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            placeholder="0,00"
                          />
                        </div>

                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => removeInsumo(index)}
                            className="w-full rounded bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700"
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addInsumo}
                    className="mt-2 rounded bg-green-600 px-4 py-2 text-white transition-colors hover:bg-green-700"
                  >
                    Adicionar Insumo
                  </button>
                </div>

                {/* Linha 3: Observações */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    rows={4}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Informações adicionais sobre o manejo (dosagem, condições, resultados, etc.)..."
                  />
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Animal ou Tipo de Manejo</label>
                  <input
                    type="text"
                    placeholder="Digite o SISBOV, nome do animal ou tipo de manejo..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
                
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Data Início</label>
                  <input
                    type="date"
                    value={filtros.data_inicio}
                    onChange={(e) => setFiltros({...filtros, data_inicio: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
                
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                  <input
                    type="date"
                    value={filtros.data_fim}
                    onChange={(e) => setFiltros({...filtros, data_fim: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Eventos de Manejo */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {Array.isArray(eventosFiltrados) ? eventosFiltrados.length : 0} evento(s) encontrado(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Data</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Animal</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Tipo de Manejo</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Observações</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(eventosFiltrados) && eventosFiltrados.map((evento) => (
                        <tr key={evento.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{evento.id}</td>
                          <td className="p-3 text-sm">{formatarData(evento.data_manejo)}</td>
                          <td className="p-3 text-sm">{evento.animal_sisbov} - {evento.animal_nome}</td>
                          <td className="p-3 text-sm">
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                              {getTipoManejoNome(evento)}
                            </span>
                          </td>
                          <td className="p-3 text-sm max-w-xs truncate">{evento.observacoes || "—"}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(evento)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(evento.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Excluir"
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      
                      {(!Array.isArray(eventosFiltrados) || eventosFiltrados.length === 0) && (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-gray-500">
                            {filtros.busca || filtros.data_inicio || filtros.data_fim
                              ? "Nenhum evento encontrado com os filtros selecionados."
                              : "Nenhum evento de manejo cadastrado."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ModalCadastroAnimal
        isOpen={mostrarModalAnimal}
        onClose={() => setMostrarModalAnimal(false)}
        onSuccess={async (novo) => {
          await carregarAnimais();
          setFormData((prev) => ({ ...prev, animal: novo.id }));
        }}
      />
      <ModalCadastroTipoManejo
        isOpen={mostrarModalTipoManejo}
        onClose={() => setMostrarModalTipoManejo(false)}
        onSuccess={async (novo) => {
          await carregarTiposManejo();
          setFormData((prev) => ({ ...prev, tipo_manejo: novo.id }));
        }}
      />
    </Layout>
  );
}